import React, { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
} from '@mui/material';
import { ExpandMore, Refresh } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../../../../shared/Settings/appSettings';
import { IOSSwitch } from '../../../../shared/Components/iosSwitch';
import { StyledIconButton } from '../../../../shared/Components/StyledIconButton';
import type { EventScoreWithId, TbRegistration, CourseHoleInfo } from '@moment-golf/types';
import { useTBContext } from '../../../Contexts/useTBStates';
import { RoundButton } from '../../TabComponents';
import { buildTiedGroups, type TiedGroup } from '../../../utils/playoffUtils';
import { effectiveRoundBool } from '../../Functions/applyUniformStartHoleToViewEventData';
import {
    formatHoleSlotLabel,
    getShotgunSlotSortIndex,
    normalizeShotgunOrder,
    resolveRoundHoles,
    type ShotgunSlotOrder,
} from '../../Functions/shotgunTeeSheetUtils';

interface PlayoffPanelProps {
    appDims: any;
    compHeights: any;
}

/** Row shape produced by mapScoreToHole (score grid) */
interface RoundScoreRow {
    _id: string;
    playerName: string;
    division: string;
    flight: string;
    teeTime: Date;
    startHole: string;
    startHoleSort: number;
    [key: number]: number | undefined;
    thru: number;
    total: number;
    scoreToPar: number;
    confirmed: boolean;
    submitted: boolean;
    widthdrawn: boolean;
    disqualified: boolean;
    indPlayoffWin: boolean;
    teamPlayoffWin: boolean;
    finalRank: { gross: number; net: number } | number;
    finalTeamRank: { gross: number; net: number } | number;
}

let roundHoles: CourseHoleInfo[] = [];

export const PlayoffPanel = ({ appDims, compHeights }: PlayoffPanelProps) => {
    const auth = useAuth();
    const api = CreateApi();

    const {
        factor,
        appTheme,
        editMode,
        roundValue,
        setRoundValue,
        viewEvent,
        viewEventData,
        originalViewEventData,
        originalScores,
    } = useTBContext();

    const [roundScoreData, setRoundScoreData] = useState<any[]>([]);
    const [lastScoreQuery, setLastScoreQuery] = useState(new Date(Date.UTC(2000, 0, 1)));
    const [scoreFormatToggle, setScoreFormatToggle] = useState('gross');
    const [confirmingGroup, setConfirmingGroup] = useState<string | null>(null);

    useEffect(() => {
        if (viewEvent) {
            const firstRound = viewEvent.rounds?.[0];
            const formatValue = firstRound?.scoreFormat?.toLowerCase?.() !== 'net' ? 'gross' : 'net';
            setScoreFormatToggle(formatValue);
        }
    }, [viewEvent]);

    useEffect(() => {
        if (
            viewEvent &&
            viewEventData &&
            viewEventData.scores &&
            viewEventData.scores.length > 0 &&
            roundValue &&
            roundValue > 0
        ) {
            roundHoles = resolveRoundHoles(viewEvent, viewEventData, roundValue);
            setRoundScoreData(mapScoreToHole());
        }
    }, [roundValue, viewEventData.scores, scoreFormatToggle]);

    const refreshScores = async () => {
        if (!viewEvent?._id) return;
        await api
            .get(`/tb/GetEventScores`, {
                params: {
                    _id: viewEvent._id,
                    referenceDate: lastScoreQuery,
                },
            })
            .then((res: { data: EventScoreWithId[] }) => {
                const updatedScores = JSON.parse(JSON.stringify(viewEventData.scores));
                updatedScores.forEach((score: EventScoreWithId, index: number) => {
                    const matchingScore = res.data.find((s) => s._id === score._id);

                    if (matchingScore) {
                        updatedScores[index] = matchingScore;
                    }
                });

                setRoundScoreData(mapScoreToHole(updatedScores));
                viewEventData.scores = JSON.parse(JSON.stringify(updatedScores));
                originalViewEventData.current.scores = JSON.parse(JSON.stringify(updatedScores));
            })
            .catch((err: any) => {
                console.error('Error refreshing scores', err);
            });
        setLastScoreQuery(new Date());
    };

    function mapScoreToHole(scores?: EventScoreWithId[], registrations?: TbRegistration[], round?: number) {
        const internalRoundValue = round ?? roundValue;
        const scoreFormat = viewEvent?.rounds[internalRoundValue - 1]?.scoreFormat?.toLowerCase?.() ?? '';
        const scoresToMap = scores ?? viewEventData.scores;
        const registrationsToMap = registrations ?? viewEventData.registrations;
        const isShotgun = effectiveRoundBool(viewEvent?.rounds?.[internalRoundValue - 1]?.shotgunStart);
        const viewEventDataForSort =
            registrations !== undefined && scores !== undefined
                ? { courseData: viewEventData.courseData ?? [] }
                : viewEventData;

        const mappedRoundScoreData: RoundScoreRow[] = [];

        scoresToMap.forEach((score: EventScoreWithId) => {
            const regsForScore = registrationsToMap.filter((reg) => reg.eventScoreID === score._id);
            const matchingReg = regsForScore[0];
            const teeSlot =
                matchingReg?.teeTimes?.find((t) => Number(t.round) === internalRoundValue) ??
                matchingReg?.teeTimes?.[internalRoundValue - 1];
            const teeTimeStr = teeSlot?.teeTime;

            const rs = score.roundScores[internalRoundValue - 1];
            const startingHole = rs?.startingHole ?? teeSlot?.startingHole;
            const shotgunOrderRaw = rs?.shotgunOrder ?? teeSlot?.shotgunOrder ?? null;
            const shotgunOrder: ShotgunSlotOrder = normalizeShotgunOrder(shotgunOrderRaw);

            const divisionVals = regsForScore
                .map((reg) => reg.division)
                .filter((d): d is string => typeof d === 'string' && d.trim() !== '');
            const flightVals = regsForScore
                .map((reg) => reg.flight)
                .filter((f): f is string => typeof f === 'string' && f.trim() !== '');
            const divisionLabel = Array.from(new Set(divisionVals)).join(' / ');
            const flightLabel = Array.from(new Set(flightVals)).join(' / ');

            const roundScore: RoundScoreRow = {
                _id: score._id ?? '',
                playerName: registrationsToMap
                    .filter((reg) => reg.eventScoreID === score._id)
                    .map((reg) => reg.playerName)
                    .join(' / '),
                division: divisionLabel,
                flight: flightLabel,
                teeTime: teeTimeStr ? new Date(teeTimeStr) : new Date(0),
                startHole:
                    isShotgun && startingHole != null
                        ? formatHoleSlotLabel(Number(startingHole), shotgunOrder)
                        : '',
                startHoleSort:
                    isShotgun && startingHole != null
                        ? getShotgunSlotSortIndex(
                              viewEvent,
                              viewEventDataForSort,
                              internalRoundValue,
                              Number(startingHole),
                              shotgunOrderRaw,
                          )
                        : 0,
                total: 0,
                thru: 0,
                scoreToPar: 0,
                confirmed: false,
                submitted: false,
                widthdrawn: false,
                disqualified: false,
                indPlayoffWin: false,
                teamPlayoffWin: false,
                finalRank: 0,
                finalTeamRank: 0,
            };

            score.roundScores[internalRoundValue - 1].holeScores.forEach((hole) => {
                roundScore[hole.holeNumber] = hole.playerScore;
                if (hole.playerScore > 0) {
                    roundScore.scoreToPar +=
                        hole.playerScore -
                        (roundHoles[hole.holeNumber - 1]?.par ?? 0) -
                        (scoreFormat !== 'gross' && scoreFormatToggle === 'net' ? (hole.playerHcpAdj ?? 0) : 0);
                    roundScore.thru += 1;
                }
            });

            const total = score.roundScores[internalRoundValue - 1].holeScores.reduce(
                (acc, hole) => acc + hole.playerScore,
                0,
            );

            roundScore.confirmed = rs.confirmed;
            roundScore.submitted = rs.submitted;
            roundScore.widthdrawn = rs.withdrawn ?? (rs as { widthdrawn?: boolean }).widthdrawn ?? false;
            roundScore.disqualified = rs.disqualified;
            roundScore.indPlayoffWin = score.indPlayoffWin;
            roundScore.teamPlayoffWin = score.teamPlayoffWin;

            if (typeof score.finalRank === 'number') {
                roundScore.finalRank = { gross: score.finalRank, net: score.finalRank };
            } else if (score.finalRank && typeof score.finalRank === 'object') {
                roundScore.finalRank = {
                    gross: score.finalRank.gross || 0,
                    net: score.finalRank.net || 0,
                };
            } else {
                roundScore.finalRank = { gross: 0, net: 0 };
            }

            if (typeof score.finalTeamRank === 'number') {
                roundScore.finalTeamRank = { gross: score.finalTeamRank, net: score.finalTeamRank };
            } else if (score.finalTeamRank && typeof score.finalTeamRank === 'object') {
                roundScore.finalTeamRank = {
                    gross: score.finalTeamRank.gross || 0,
                    net: score.finalTeamRank.net || 0,
                };
            } else {
                roundScore.finalTeamRank = { gross: 0, net: 0 };
            }

            roundScore.total = total;
            mappedRoundScoreData.push(roundScore);
        });

        mappedRoundScoreData.sort((a, b) => {
            if (isShotgun) {
                if (a.startHoleSort !== b.startHoleSort) return a.startHoleSort - b.startHoleSort;
                return a.playerName.localeCompare(b.playerName);
            }
            if (a.teeTime < b.teeTime) return -1;
            if (a.teeTime > b.teeTime) return 1;
            return a.playerName.localeCompare(b.playerName);
        });

        originalScores.current = JSON.parse(JSON.stringify(mappedRoundScoreData));
        return mappedRoundScoreData;
    }

    /** Builds columns for the per-group playoff DataGridPro. */
    const createPlayoffGroupColumns = (group: TiedGroup) => {
        const holes = roundHoles;
        const scoreFormat = viewEvent?.rounds[roundValue - 1]?.scoreFormat?.toLowerCase() ?? '';

        const resultMap = new Map(group.results.map((r) => [r.row._id, r]));

        const sortedResults = [...group.results].sort((a, b) => a.playoffRank - b.playoffRank);

        const holesInProcessingOrder = [...holes].sort(
            (a, b) => (a.hdcp ?? 999) - (b.hdcp ?? 999) || a.number - b.number,
        );
        const processingOrderMap = new Map(holesInProcessingOrder.map((h, i) => [h.number, i]));
        const getProcessingOrder = (holeNum: number | null) =>
            holeNum === null ? -1 : (processingOrderMap.get(holeNum) ?? 999);

        const highlightMap = new Map<string, { hole: number; isGreen: boolean } | null>();

        for (const result of sortedResults) {
            const nextBelow = sortedResults.find((r) => r.playoffRank > result.playoffRank);
            const winningHole = nextBelow?.decidingHole ?? null;
            const losingHole = result.decidingHole;

            const winOrder = getProcessingOrder(winningHole);
            const loseOrder = getProcessingOrder(losingHole);

            if (winningHole !== null && (losingHole === null || winOrder >= loseOrder)) {
                highlightMap.set(result.row._id, { hole: winningHole, isGreen: true });
            } else if (losingHole !== null) {
                highlightMap.set(result.row._id, { hole: losingHole, isGreen: false });
            } else {
                highlightMap.set(result.row._id, null);
            }
        }

        const rankLabel = (playoffRank: number) => {
            const rankPrefix = scoreFormat === 'both' ? (scoreFormatToggle === 'gross' ? 'G' : 'N') : '';
            const isTied = group.results.filter((r) => r.playoffRank === playoffRank).length > 1;
            const displayRank = group.baseRank + playoffRank - 1;
            return `${rankPrefix}${isTied ? 'T' : ''}${ordinal(displayRank)}`;
        };

        const wrapCell = (params: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{params.value}</Box>
        );

        return [
            { field: '_id', headerName: 'ID', flex: 1 },
            {
                field: 'playoffRank',
                headerName: 'Rank',
                flex: 1,
                minWidth: 60,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params: any) => {
                    const result = resultMap.get(params.row._id);
                    const rank = result?.playoffRank ?? 0;
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: result?.isWinner ? 700 : 400,
                                color: result?.isWinner ? 'success.main' : 'text.primary',
                            }}
                        >
                            {rankLabel(rank)}
                        </Box>
                    );
                },
            },
            {
                field: 'playerName',
                headerName: 'Player Name',
                flex: 2,
                minWidth: 120,
                sortable: false,
                filterable: false,
                renderCell: (params: any) => {
                    const result = resultMap.get(params.row._id);
                    return (
                        <Box sx={{ width: '100%', height: '100%', alignContent: 'center' }}>
                            <Typography
                                sx={{
                                    fontSize: 12 * factor,
                                    fontWeight: result?.isWinner ? 700 : 500,
                                    textAlign: 'left',
                                    whiteSpace: 'wrap',
                                }}
                                color='primary.main'
                            >
                                {params.value}
                            </Typography>
                        </Box>
                    );
                },
            },
            ...Array.from({ length: Number(viewEvent?.rounds[roundValue - 1]?.totalHoles || 18) }, (_, i) => i + 1)
                .map((number) => ({ number, hdcp: holes[number - 1]?.hdcp ?? 999 }))
                .sort((a, b) => a.hdcp - b.hdcp)
                .map(({ number }) => ({
                    field: number.toString(),
                    headerName: `${number}`,
                    flex: 1,
                    minWidth: 56,
                    type: 'number' as const,
                    editable: false,
                    sortable: false,
                    disableColumnMenu: true,
                    renderHeader: () => (
                        <Stack direction='column' spacing={0} alignItems='center' justifyContent='center'>
                            <Typography sx={{ fontSize: 12 * factor, fontWeight: 500 }} color='primary.main'>
                                {number}
                            </Typography>
                            <Typography sx={{ fontSize: 11 * factor }}>Par {holes[number - 1]?.par || 0}</Typography>
                            <Typography sx={{ fontSize: 11 * factor }}>hcp {holes[number - 1]?.hdcp || 0}</Typography>
                        </Stack>
                    ),
                    renderCell: (params: any) => {
                        const hl = highlightMap.get(params.row._id);
                        const active = hl?.hole === number;
                        const circleBg = active
                            ? hl!.isGreen
                                ? 'rgba(76,175,80,0.35)'
                                : 'rgba(244,67,54,0.25)'
                            : 'transparent';
                        return (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        backgroundColor: circleBg,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: active ? 600 : 400,
                                        fontSize: 12 * factor,
                                    }}
                                >
                                    {params.value}
                                </Box>
                            </Box>
                        );
                    },
                })),
            {
                field: 'total',
                headerName: 'Total',
                flex: 1,
                minWidth: 60,
                sortable: false,
                disableColumnMenu: true,
                renderCell: wrapCell,
            },
            {
                field: 'scoreToPar',
                headerName: 'To Par',
                flex: 1,
                minWidth: 60,
                sortable: false,
                disableColumnMenu: true,
                renderCell: wrapCell,
            },
        ];
    };

    const handleConfirmPlayoff = async (group: TiedGroup) => {
        const groupKey = `${group.division}||${group.flight}||${group.scoreToPar}`;
        setConfirmingGroup(groupKey);

        const scoreFormat = viewEvent?.rounds[roundValue - 1]?.scoreFormat?.toLowerCase() ?? 'gross';
        const updateGross = scoreFormat === 'gross' || (scoreFormat === 'both' && scoreFormatToggle === 'gross');
        const updateNet = scoreFormat === 'net' || (scoreFormat === 'both' && scoreFormatToggle === 'net');

        try {
            const payload = group.results.map((r) => {
                const fr =
                    typeof r.row.finalRank === 'object' && r.row.finalRank && 'gross' in r.row.finalRank
                        ? r.row.finalRank
                        : { gross: 0, net: 0 };
                const ftr =
                    typeof r.row.finalTeamRank === 'object' &&
                    r.row.finalTeamRank &&
                    'gross' in r.row.finalTeamRank
                        ? r.row.finalTeamRank
                        : { gross: 0, net: 0 };
                const resolvedRank = group.baseRank + r.playoffRank - 1;
                return {
                    eventScoreID: r.row._id,
                    roundIndex: roundValue - 1,
                    confirmed: r.row.confirmed,
                    submitted: r.row.submitted,
                    widthdrawn: r.row.widthdrawn,
                    disqualified: r.row.disqualified,
                    indPlayoffWin: r.isWinner,
                    teamPlayoffWin: false,
                    finalRank: {
                        gross: updateGross ? resolvedRank : Number(fr.gross),
                        net: updateNet ? resolvedRank : Number(fr.net),
                    },
                    finalTeamRank: { gross: Number(ftr.gross), net: Number(ftr.net) },
                };
            });
            await api.post('/tb/ConfirmSubmitScores', {
                userID: auth.user?.profile.sub,
                eventScores: payload,
            });
            await refreshScores();
        } catch (err) {
            console.error('Playoff confirm failed', err);
        } finally {
            setConfirmingGroup(null);
        }
    };

    const ordinal = (n: number) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
    };

    if (!viewEvent) {
        return null;
    }

    const totalHoles = Number(viewEvent?.rounds[roundValue - 1]?.totalHoles || 18);
    const qualifiedScores = roundScoreData.filter(
        (row: any) => row.confirmed === true && row.thru >= totalHoles,
    );

    const flighting = (viewEvent as any)?.flighting;
    const divisionsList: any[] = flighting?.divisions ?? [];
    const flightsList: any[] = flighting?.flights ?? [];

    const rawTiedGroups = buildTiedGroups(qualifiedScores, roundHoles, {
        hasFlights: flightsList.length > 0,
        divisionWideBaseRank:
            flightsList.length === 0 || viewEvent?.rounds?.[roundValue - 1]?.pointSystem === 'Series',
    });

    const divIndex = (name: string) => {
        const i = divisionsList.findIndex((d: any) => d.divisionName === name);
        return i === -1 ? 9999 : i;
    };
    const flightIndex = (name: string) => {
        const i = flightsList.findIndex((f: any) => f.flightName === name);
        return i === -1 ? 9999 : i;
    };

    const tiedGroups = [...rawTiedGroups].sort((a, b) => {
        const divCmp = divIndex(a.division) - divIndex(b.division);
        if (divCmp !== 0) return divCmp;
        const flightCmp = flightIndex(a.flight) - flightIndex(b.flight);
        if (flightCmp !== 0) return flightCmp;
        return a.scoreToPar - b.scoreToPar;
    });

    const anyFlight = tiedGroups.some((g) => g.flight.trim() !== '');

    const formatScore = (s: number) => (s > 0 ? `+${s}` : s === 0 ? 'E' : `${s}`);

    return (
        <Stack
            direction='column'
            spacing={0}
            alignItems='center'
            justifyContent='flex-start'
            sx={{ width: '100%', height: '100%', padding: '0px', overflow: 'auto' }}
        >
            <Stack
                direction='row'
                gap={'20px'}
                marginTop={1}
                width={compHeights.appUsableWidth - 60}
                maxHeight={35}
                padding={'10px 40px'}
                alignItems='center'
                justifyContent='flex-end'
            >
                <Typography variant='h6' sx={{ flex: 1, textAlign: 'left', fontWeight: 500 }}>
                    Scorecard Playoffs
                </Typography>
                <StyledIconButton
                    appTheme={appTheme}
                    label='Refresh Scores'
                    factor={factor}
                    color='primary'
                    icon={<Refresh />}
                    width={'max-content'}
                    height={40}
                    onClick={refreshScores}
                />
                <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>
                    Round
                </Typography>
                <Stack direction='row' spacing={0} padding={'0px 0px'} alignItems='center' justifyContent='flex-end'>
                    {Array.from({ length: Number(viewEvent?.totalRounds ?? 0) }, (_, index) => index + 1).map(
                        (number) => (
                            <RoundButton
                                key={`${roundValue}-${number}`}
                                number={number}
                                roundValue={roundValue}
                                editMode={editMode}
                                appTheme={appTheme}
                                factor={factor}
                                setRoundValue={setRoundValue}
                            />
                        ),
                    )}
                </Stack>
                {viewEvent?.rounds &&
                    viewEvent.rounds.length > 0 &&
                    roundValue > 0 &&
                    viewEvent.rounds[roundValue - 1]?.scoreFormat.toLowerCase() === 'both' && (
                        <Stack direction='row' spacing={2} justifyContent='center' mb={0.5} alignItems='center'>
                            <Typography variant='subtitle2' sx={{ width: 'max-content' }}>
                                Gross
                            </Typography>
                            <IOSSwitch
                                disabled={false}
                                checked={scoreFormatToggle === 'net'}
                                onChange={() =>
                                    setScoreFormatToggle(scoreFormatToggle === 'net' ? 'gross' : 'net')
                                }
                            />
                            <Typography variant='subtitle2' sx={{ width: 'max-content' }}>
                                Net
                            </Typography>
                        </Stack>
                    )}
            </Stack>

            <Stack
                direction='column'
                spacing={1}
                alignItems='stretch'
                sx={{ width: compHeights.appUsableWidth - 60, padding: '8px 40px', overflow: 'auto' }}
            >
                {tiedGroups.length === 0 && (
                    <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
                        No ties detected for this round.
                    </Typography>
                )}

                {tiedGroups.map((group) => {
                    const groupKey = `${group.division}||${group.flight}||${group.scoreToPar}`;
                    const isConfirming = confirmingGroup === groupKey;

                    const parts: string[] = [];
                    if (group.division) parts.push(group.division);
                    if (anyFlight) parts.push(group.flight || '—');
                    parts.push(formatScore(group.scoreToPar));
                    parts.push(`${group.players.length} tied for ${ordinal(group.baseRank)}`);
                    const summaryText = parts.join('  |  ');

                    const gridRows = group.results
                        .map((r) => ({
                            ...r.row,
                            playoffRank: r.playoffRank,
                        }))
                        .sort((a, b) => a.playoffRank - b.playoffRank);

                    const gridHeight = Math.min(group.players.length * 56 + 110, 420);

                    return (
                        <Accordion
                            key={groupKey}
                            defaultExpanded={false}
                            disableGutters
                            sx={{
                                border: `1px solid ${appTheme.palette.primary.light}`,
                                borderRadius: 1,
                                '&:before': { display: 'none' },
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Stack direction='row' spacing={2} alignItems='center' sx={{ width: '100%' }}>
                                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                                        {summaryText}
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: '8px 16px 16px' }}>
                                <Box sx={{ height: gridHeight, width: '100%' }}>
                                    <DataGridPro
                                        key={`playoff-group-${groupKey}-${roundValue}`}
                                        rows={gridRows}
                                        getRowId={(row) => row._id}
                                        columns={createPlayoffGroupColumns(group)}
                                        editMode='row'
                                        disableRowSelectionOnClick
                                        hideFooter
                                        initialState={{
                                            columns: {
                                                columnVisibilityModel: { _id: false },
                                            },
                                            pinnedColumns: { left: ['playoffRank', 'playerName'] },
                                        }}
                                        sx={{
                                            fontSize: 12 * factor,
                                            backgroundColor: 'sepia.background',
                                            '& .MuiDataGrid-main': { backgroundColor: 'sepia.background' },
                                            '& .MuiDataGrid-virtualScroller': { backgroundColor: 'sepia.background' },
                                            '& .MuiDataGrid-columnHeader': {
                                                backgroundColor: 'sepia.background',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            },
                                            '& .MuiDataGrid-columnHeaderTitleContainer': { justifyContent: 'center' },
                                            '& .MuiDataGrid-columnHeaderTitle': {
                                                fontWeight: 500,
                                                fontSize: 12 * factor,
                                                color: 'primary.main',
                                                textAlign: 'center',
                                            },
                                            '& .MuiDataGrid-cell': { backgroundColor: 'sepia.background' },
                                            '& .MuiDataGrid-row': { backgroundColor: 'sepia.background' },
                                        }}
                                    />
                                </Box>
                                <Stack direction='row' justifyContent='flex-end' sx={{ mt: 1 }}>
                                    <Button
                                        variant='contained'
                                        size='small'
                                        disabled={isConfirming}
                                        onClick={() => handleConfirmPlayoff(group)}
                                        startIcon={
                                            isConfirming ? <CircularProgress size={14} color='inherit' /> : null
                                        }
                                        sx={{ borderRadius: 1, minWidth: 110 }}
                                    >
                                        {isConfirming ? 'Saving…' : 'Confirm'}
                                    </Button>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>
        </Stack>
    );
};
